import { useState, useEffect, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import FlavorGraphCanvas, { toElements } from "@/components/FlavorGraphCanvas";
import InspectorPanel from "@/components/InspectorPanel";
import ChatView from "@/components/ChatView";
import TeiaIntro from "@/components/TeiaIntro";
import { useSouschef } from "@/hooks/useSouschef";
import { getNetwork, getSyncIngredients, syncPull } from "@/lib/atlasApi";
import { Layout, Search, Bot, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function mergeElements(prev, incoming) {
  const map = new Map();
  [...prev, ...incoming].forEach((el) => map.set(el.data.id, el));
  return Array.from(map.values());
}

export default function CanvasPage() {
  const { canExport } = useOutletContext();
  const [elements, setElements] = useState([]);
  const [selected, setSelected] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [layoutKey, setLayoutKey] = useState(0);
  const [search, setSearch] = useState("sop-tomate");
  const [rootSop, setRootSop] = useState("sop-tomate");
  const [error, setError] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [intro, setIntro] = useState(true);
  const [introReady, setIntroReady] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const nameIndex = useMemo(() => {
    const idx = {};
    elements.forEach((el) => { if (el.data.label) idx[el.data.label.toLowerCase()] = el.data.id; });
    return idx;
  }, [elements]);

  const loadNetwork = useCallback(async (sopId, replace = false, depth = 1) => {
    setError("");
    try {
      const g = await getNetwork(sopId, depth, 80);
      setElements((prev) => (replace ? toElements(g) : mergeElements(prev, toElements(g))));
      if (replace) setRootSop(sopId);
      setLayoutKey((k) => k + 1);
    } catch (e) {
      setError(e?.response?.status === 404 ? `Insumo "${sopId}" não encontrado.` : "Falha ao carregar o grafo.");
    }
  }, []);

  // Carrega a Teia a partir do GRAFO, centrando no insumo REAL do utilizador
  // (os ids ao vivo do SOP & RAFT viram sop_raft_id no grafo após o pull-sync).
  // Nunca bloqueia: se o id real ainda não estiver no grafo, usa a teia-semente.
  const refreshTeia = useCallback(async () => {
    const tryLoad = async (id) => {
      try { const g = await getNetwork(id, 2, 80); return g?.nodes?.length ? g : null; }
      catch (_) { return null; }
    };
    let root = "sop-tomate";
    let g = null;
    try {
      const sync = await getSyncIngredients();
      const live = (sync?.ingredients || []).find((x) => x?.sop_raft_id || x?.id);
      const liveId = live?.sop_raft_id || live?.id;
      if (liveId) { g = await tryLoad(liveId); if (g) root = liveId; }
    } catch (_) { /* sem dados ao vivo ainda: usa a semente */ }
    if (!g) g = await tryLoad("sop-tomate");
    if (g) {
      setElements(toElements(g));
      setRootSop(root);
      setSearch(root);
      setLayoutKey((k) => k + 1);
    }
  }, []);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      const r = await syncPull();
      const ing = r?.ingredients?.persisted ?? 0;
      const rec = r?.recipes?.persisted ?? 0;
      await refreshTeia();
      toast.success(`Sincronizado com o SOP & RAFT: ${ing} insumos, ${rec} fichas técnicas.`);
    } catch (_) {
      toast.error("Falha ao sincronizar com o SOP & RAFT.");
    } finally {
      setSyncing(false);
    }
  }, [refreshTeia]);

  // Ao entrar na Teia: mostra já o grafo atual e, em seguida, puxa os dados reais
  // do utilizador (pull-sync) e recarrega — a intro procedural anima o desabrochar.
  useEffect(() => {
    (async () => {
      await refreshTeia();
      setIntroReady(true);
      setSyncing(true);
      try { await syncPull(); await refreshTeia(); } catch (_) { /* segue com o que houver */ }
      finally { setSyncing(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTeia]);

  const handleGraphContext = useCallback((gc) => {
    if (gc?.nodes?.length) setElements((prev) => mergeElements(prev, toElements(gc)));
    if (gc?.entities_matched?.length) {
      const first = gc.entities_matched[0]?.toLowerCase();
      setElements((prev) => { const id = prev.find((e) => e.data.label?.toLowerCase() === first)?.data.id; if (id) setHighlightId(id); return prev; });
    }
  }, []);

  const souschef = useSouschef({ onGraphContext: handleGraphContext });

  const centerId = useMemo(() => {
    const el = elements.find((e) => e.data?.sop_raft_id === rootSop);
    return el?.data?.id;
  }, [elements, rootSop]);

  const handleEntityClick = useCallback((name) => {
    const id = nameIndex[name.toLowerCase()];
    if (id) { setHighlightId(id); const el = elements.find((e) => e.data.id === id); if (el) setSelected(el.data); }
  }, [nameIndex, elements]);

  return (
    <div className="h-full flex flex-col relative" data-testid="canvas-page">
      {intro && <TeiaIntro elements={elements} centerId={centerId} ready={introReady} onDone={() => setIntro(false)} />}
      <header className="flex items-center justify-between px-5 h-12 border-b border-[#2A364F] bg-[#0B0F17] shrink-0">
        <span className="text-sm font-medium text-slate-200">Teia de Sabores</span>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
            <Input
              data-testid="workbench-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadNetwork(search.trim(), true)}
              placeholder="sop_raft_id…"
              className="pl-8 h-9 w-52 bg-[#0B0F17] border-[#2A364F] text-sm"
            />
          </div>
          <Button data-testid="workbench-relayout" variant="outline" size="sm" onClick={() => setLayoutKey((k) => k + 1)} className="border-[#2A364F]">
            <Layout className="h-4 w-4" />
          </Button>
          <Button
            data-testid="teia-sync-button"
            variant="outline"
            size="sm"
            onClick={handleSync}
            disabled={syncing}
            className="border-[#2A364F] text-slate-300"
            title="Sincronizar insumos e fichas técnicas do SOP & RAFT"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sincronizando…" : "Sincronizar"}
          </Button>
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <Button data-testid="toggle-canvas-souschef-drawer" size="sm" className="bg-[#E05A2B] hover:bg-[#F06B3C] text-white">
                <Bot className="h-4 w-4 mr-1.5" /> SousChef
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="p-0 w-full sm:w-[460px] bg-[#0B0F17] border-[#2A364F] flex flex-col" data-testid="canvas-souschef-drawer">
              <SheetTitle className="sr-only">SousChef AI</SheetTitle>
              <div className="px-4 h-12 flex items-center gap-2 border-b border-[#2A364F] shrink-0">
                <Bot className="h-4 w-4 text-[#E05A2B]" />
                <span className="text-sm font-medium text-slate-200">SousChef AI · rápido</span>
              </div>
              <div className="flex-1 min-h-0">
                <ChatView souschef={souschef} onEntityClick={handleEntityClick} canExport={canExport} compact placeholder="Ex: Com o que o tomate combina?" />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {error && <div className="px-5 py-2 text-sm text-[#FF007A] bg-[#FF007A]/10" data-testid="workbench-error">{error}</div>}

      <PanelGroup direction="horizontal" className="flex-1">
        <Panel defaultSize={66} minSize={40}>
          <div className="h-full relative bg-[#0B0F17]">
            <div className="absolute top-3 left-3 z-10 flex gap-3 text-[11px] bg-[#161E2E]/85 backdrop-blur rounded-lg px-3 py-2 border border-[#2A364F]">
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#22C55E" }} />Insumo</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5" style={{ background: "#00E5FF" }} />Composto</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rotate-45" style={{ background: "#EC4899" }} />Categoria</span>
            </div>
            <FlavorGraphCanvas
              elements={elements}
              onSelect={setSelected}
              onExpand={(sopId) => loadNetwork(sopId, false)}
              highlightId={highlightId}
              layoutKey={layoutKey}
              centerId={centerId}
            />
          </div>
        </Panel>

        <PanelResizeHandle className="w-1.5 bg-[#2A364F] hover:bg-[#E05A2B]/60 transition-colors" />

        <Panel defaultSize={34} minSize={24}>
          <div className="h-full bg-[#0B0F17] border-l border-[#2A364F] overflow-y-auto">
            <div className="px-5 h-12 flex items-center border-b border-[#2A364F]">
              <span className="text-sm font-medium text-slate-200">Inspetor</span>
            </div>
            <InspectorPanel node={selected} onExpand={(sopId) => loadNetwork(sopId, false)} canExport={canExport} />
          </div>
        </Panel>
      </PanelGroup>
    </div>
  );
}
